import React, { useEffect, useState, useCallback } from "react";
import { FaUser, FaEnvelope, FaUserTag, FaExclamationTriangle } from 'react-icons/fa';
import styles from './ProfileComponent.module.css';

const ProfileComponent = ({ user }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Función para mostrar el nombre del rol en formato legible
  const formatRole = useCallback((role) => {
    if (!role) return "Sin asignar";
    const lowerRole = role.toLowerCase();
    switch (lowerRole) {
      case "admin":
        return "Administrador";
      case "user":
        return "Usuario";
      default:
        return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    }
  }, []);

  // Función para obtener las iniciales del usuario
  const getUserInitials = useCallback((displayName, email) => {
    if (displayName && displayName.trim()) {
      const names = displayName.trim().split(' ').filter(name => name.length > 0);
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase();
      }
      return names[0].substring(0, 2).toUpperCase();
    }
    if (email && email.trim()) {
      const emailName = email.split('@')[0];
      return emailName.substring(0, 2).toUpperCase();
    }
    return "US";
  }, []);

  // Función para obtener el color del badge según el rol
  const getRoleColor = useCallback((role) => {
    if (!role) return styles.roleDefault;
    const lowerRole = role.toLowerCase();
    switch (lowerRole) {
      case "admin":
        return styles.roleAdmin;
      case "user":
        return styles.roleUser;
      default:
        return styles.roleDefault;
    }
  }, []);

  // Función mejorada para manejar URLs de Google
  const getOptimizedImageUrl = useCallback((photoURL) => {
    if (!photoURL) return null;
    
    // Si es una URL de Google, optimizarla para mejor carga
    if (photoURL.includes('googleusercontent.com') || photoURL.includes('googleapis.com')) {
      // Añadir parámetros para imagen de tamaño apropiado
      const url = new URL(photoURL);
      url.searchParams.set('sz', '200'); // Tamaño 200x200
      return url.toString();
    }
    
    return photoURL;
  }, []);

  // Precarga de la imagen con fallback mejorado
  useEffect(() => {
    const photoURL = user?.photoURL;
    
    if (!photoURL) {
      setImageLoaded(true);
      setImageError(true);
      return;
    }

    setImageLoaded(false);
    setImageError(false);

    const optimizedUrl = getOptimizedImageUrl(photoURL);
    const img = new Image();
    
    const handleLoad = () => {
      setImageLoaded(true);
      setImageError(false);
    };
    
    const handleError = () => {
      setImageLoaded(true);
      setImageError(true);
    };

    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);
    
    // Configurar crossOrigin para imágenes de Google
    if (optimizedUrl.includes('google')) {
      img.crossOrigin = 'anonymous';
    }
    
    img.src = optimizedUrl;

    return () => {
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
    };
  }, [user?.photoURL, getOptimizedImageUrl]);

  // Control de carga general del componente
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  // Validación de props
  if (!user) {
    return (
      <div className={styles.profileCard}>
        <div className={styles.profileError}>
          <FaExclamationTriangle className={styles.errorIcon} />
          <p>No se pudo cargar la información del usuario</p>
        </div>
      </div>
    );
  }

  const displayName = user.displayName?.trim() || "Usuario";
  const email = user.email?.trim() || "Sin correo electrónico";
  const role = user.role || null;

  return (
    <div className={`${styles.profileCard} ${isLoaded ? styles.loaded : ''}`}>
      <div className={styles.profileHeader}>
        <div className={styles.avatarContainer}>
          {imageLoaded && !imageError && user.photoURL ? (
            <img
              src={getOptimizedImageUrl(user.photoURL)}
              alt={`Avatar de ${displayName}`}
              className={styles.profileAvatar}
              loading="lazy"
              crossOrigin="anonymous"
            />
          ) : imageLoaded ? (
            <div className={styles.avatarPlaceholder}>
              <span className={styles.initials}>
                {getUserInitials(user.displayName, user.email)}
              </span>
            </div>
          ) : (
            <div className={styles.avatarLoading}>
              <div className={styles.loadingSpinner}></div>
            </div>
          )}
        </div>
        
        <div className={styles.headerInfo}>
          <h2 className={styles.profileName}>{displayName}</h2>
          <div className={`${styles.roleBadge} ${getRoleColor(role)}`}>
            <FaUserTag className={styles.roleIcon} />
            {formatRole(role)}
          </div>
        </div>
      </div>

      <div className={styles.profileBody}>
        <div className={styles.profileDetail}>
          <FaEnvelope className={styles.detailIcon} />
          <div className={styles.detailContent}>
            <span className={styles.detailLabel}>Correo electrónico</span>
            <span className={styles.detailValue}>{email}</span>
          </div>
        </div>

        {user.lastLogin && (
          <div className={styles.profileDetail}>
            <FaUser className={styles.detailIcon} />
            <div className={styles.detailContent}>
              <span className={styles.detailLabel}>Último acceso</span>
              <span className={styles.detailValue}>
                {new Date(user.lastLogin).toLocaleString('es-ES', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          </div>
        )}

        {user.department && (
          <div className={styles.profileDetail}>
            <FaUserTag className={styles.detailIcon} />
            <div className={styles.detailContent}>
              <span className={styles.detailLabel}>Departamento</span>
              <span className={styles.detailValue}>{user.department}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileComponent;